/**
 * Formal assessment stream + per-session observations contract (PRD E1–E4;
 * migration 015; increment-7 plan PR2).
 *
 * Granular and foundational, like everything clinical: the wire carries only
 * what a human entered — TUG seconds/aid/comment, ROM integers, SLR40's four
 * components, the 24 WOMAC responses with their labels AS ADMINISTERED, and
 * the six observation fields each answered VALUE-OR-EXPLICIT-SKIP (owner
 * call, plan §0.1). Derived values (WOMAC subscales/total/normalized100/
 * band, targetMet/targetDetail) appear ONLY in read responses, computed by
 * the server on read from @advaitnetra/protocol — this package deliberately
 * does not import protocol (dependency direction: protocol builds ON shared),
 * so the derived shapes below are structural mirrors, not re-exports.
 */

import { z } from 'zod';

// ---- vocabulary -------------------------------------------------------------

/**
 * The miss path's five authored reasons (the adopted prototype's
 * SKIP_REASONS, verbatim labels). Codes are the stable identity the record
 * stores (migration 015 CHECK); labels are what the hub renders.
 */
export const ASSESSMENT_MISS_REASONS = [
  { code: 'pain-safety', label: 'Pain / safety' },
  { code: 'time-constraint', label: 'Time constraint' },
  { code: 'patient-refusal', label: 'Patient refusal' },
  { code: 'clinical-decision', label: 'Clinical decision' },
  { code: 'tech-connectivity', label: 'Tech / connectivity' },
] as const;
export type AssessmentMissReasonCode = (typeof ASSESSMENT_MISS_REASONS)[number]['code'];
export const assessmentMissReasonCodeSchema = z.enum([
  'pain-safety',
  'time-constraint',
  'patient-refusal',
  'clinical-decision',
  'tech-connectivity',
]);

export const WOMAC_SUBSCALE_NAMES = ['pain', 'stiffness', 'function'] as const;

// ---- instrument entry shapes ------------------------------------------------

/** TUG: stopwatch seconds (0.1 s stop, E1), aid vocabulary, therapist-only comment. */
export const tugEntrySchema = z.object({
  seconds: z.number().min(0).max(3600),
  aid: z.enum(['none', 'walker', 'stick']),
  /** Optional qualifier ("indoor", "single cane") — a note, never new vocabulary. */
  aidNote: z.string().trim().min(1).max(200).optional(),
  therapistComment: z.string().trim().min(1).max(2000).optional(),
});

/** Knee ROM: flexion + extension deficit, typed integers (capture ≠ input). */
export const kneeRomEntrySchema = z.object({
  flexionDeg: z.number().int().min(0).max(180),
  extensionDeficitDeg: z.number().int().min(0).max(90),
});

/** SLR40: the model's four components + note (plan §0.1 — model wins on data). */
export const slr40EntrySchema = z.object({
  achievedDeg: z.number().int().min(0).max(90),
  kneeLagDeg: z.number().int().min(0).max(90),
  holdSec: z.number().int().min(0).max(99),
  comfortable: z.boolean(),
  note: z.string().trim().min(1).max(500).optional(),
});

/**
 * One WOMAC item as administered: the label the physio actually read out
 * (pinned on the record even if the instrument data file later changes) and
 * the 0–4 response. Position in the 24-array IS the canonical item index;
 * the server derives the subscale from the canonical structure.
 */
export const womacResponseInputSchema = z.object({
  label: z.string().trim().min(1).max(200),
  response: z.number().int().min(0).max(4),
});

/** All 24 or nothing — the instrument defines no missing-data rule. */
export const womacEntrySchema = z.object({
  responses: z.array(womacResponseInputSchema).length(24),
});

// ---- file / miss requests ---------------------------------------------------

export const fileAssessmentRequestSchema = z
  .object({
    /** The timepoint's identity: the protocol day (validated against the pinned template server-side). */
    timepointDay: z.number().int().min(1),
    /** The in-call session it was administered in. */
    administeredInSessionId: z.uuid(),
    /** OQ3: the camera-reference display state — stored on EVERY record. */
    cameraReferenceVisible: z.boolean(),
    tug: tugEntrySchema.optional(),
    kneeRom: kneeRomEntrySchema.optional(),
    slr40: slr40EntrySchema.optional(),
    womac: womacEntrySchema.optional(),
    clinicalSummary: z.string().trim().min(1).max(4000).optional(),
    /** Correction: the record this filing supersedes (same episode × timepoint). */
    supersedesAssessmentId: z.uuid().optional(),
    /** Clinical time of the File act (device clock) — never defaulted. */
    occurredAt: z.iso.datetime(),
  })
  .superRefine((v, ctx) => {
    if (!v.tug && !v.kneeRom && !v.slr40 && !v.womac) {
      ctx.addIssue({
        code: 'custom',
        message: 'a filed assessment files at least one instrument',
      });
    }
  });
export type FileAssessmentRequest = z.infer<typeof fileAssessmentRequestSchema>;

export const recordAssessmentMissedRequestSchema = z.object({
  timepointDay: z.number().int().min(1),
  reason: assessmentMissReasonCodeSchema,
  note: z.string().trim().min(1).max(500).optional(),
  /** The in-call session the miss was recorded from, when there was one. */
  sessionId: z.uuid().optional(),
  occurredAt: z.iso.datetime(),
});
export type RecordAssessmentMissedRequest = z.infer<typeof recordAssessmentMissedRequestSchema>;

// ---- observation panel (E4) -------------------------------------------------

/**
 * One observation field: a number OR a one-tap "not assessed today" — the
 * seal-call's value-or-explicit-skip, made unrepresentable to half-answer.
 */
const observationField = (value: z.ZodNumber) =>
  z.object({ value: value.nullable(), skipped: z.boolean() }).superRefine((f, ctx) => {
    if ((f.value === null) !== f.skipped) {
      ctx.addIssue({
        code: 'custom',
        message: 'a field is a value or an asserted skip — never both, never neither',
      });
    }
  });

export const recordSessionObservationRequestSchema = z.object({
  painNrs: observationField(z.number().int().min(0).max(10)),
  swellingGirthCm: observationField(z.number().min(0).max(200)),
  flexionDeg: observationField(z.number().int().min(0).max(180)),
  extensionDeficitDeg: observationField(z.number().int().min(0).max(90)),
  slrHeightDeg: observationField(z.number().int().min(0).max(90)),
  slrKneeLagDeg: observationField(z.number().int().min(0).max(90)),
  note: z.string().trim().min(1).max(2000).optional(),
  /** Correction: the observation row this one supersedes (same session). */
  supersedesObservationId: z.uuid().optional(),
  occurredAt: z.iso.datetime(),
});
export type RecordSessionObservationRequest = z.infer<typeof recordSessionObservationRequestSchema>;

// ---- read shapes ------------------------------------------------------------

/** Structural mirror of the protocol package's derived WOMAC score (E2). */
export const derivedWomacSchema = z.object({
  pain: z.object({ score: z.number(), max: z.number() }),
  stiffness: z.object({ score: z.number(), max: z.number() }),
  function: z.object({ score: z.number(), max: z.number() }),
  total: z.number(),
  totalMax: z.number(),
  normalized100: z.number(),
  band: z.object({ label: z.string(), description: z.string() }),
});

/** Structural mirror of the derived target verdict (tri-state; PROPOSED rule labeled). */
export const derivedTargetSchema = z.object({
  timepoint: z.string(),
  targetText: z.string().nullable(),
  targetMet: z.boolean().nullable(),
  detail: z.array(
    z.object({
      text: z.string(),
      value: z.string().nullable(),
      met: z.boolean().nullable(),
    }),
  ),
  /** '≈'/'>' evaluate as >= — PROPOSED pending clinical sign-off (OQ4/OQ6). Surfaces MUST show the marking. */
  comparisonRuleProposed: z.literal(true),
});

export const assessmentRecordSchema = z.object({
  id: z.uuid(),
  episodeId: z.uuid(),
  timepointDay: z.number().int(),
  administeredInSessionId: z.uuid().nullable(),
  tug: tugEntrySchema.nullable(),
  kneeRom: kneeRomEntrySchema.nullable(),
  slr40: slr40EntrySchema.nullable(),
  womacResponses: z
    .array(
      z.object({
        itemIndex: z.number().int().min(0).max(23),
        subscale: z.enum(WOMAC_SUBSCALE_NAMES),
        label: z.string(),
        response: z.number().int().min(0).max(4),
      }),
    )
    .nullable(),
  clinicalSummary: z.string().nullable(),
  cameraReferenceVisible: z.boolean(),
  occurredAt: z.iso.datetime(),
  recordedAt: z.iso.datetime(),
  actor: z.string(),
  supersedes: z.uuid().nullable(),
  /** SYSTEM-DERIVED on read, never persisted (E2). Null without a complete WOMAC. */
  derivedWomac: derivedWomacSchema.nullable(),
  /** Null when the pinned template does not know the day (never a guessed verdict). */
  derivedTarget: derivedTargetSchema.nullable(),
});
export type AssessmentRecord = z.infer<typeof assessmentRecordSchema>;

export const assessmentMissedEventSchema = z.object({
  id: z.uuid(),
  episodeId: z.uuid(),
  timepointDay: z.number().int(),
  reason: assessmentMissReasonCodeSchema,
  note: z.string().nullable(),
  sessionId: z.uuid().nullable(),
  occurredAt: z.iso.datetime(),
  recordedAt: z.iso.datetime(),
  actor: z.string(),
});
export type AssessmentMissedEvent = z.infer<typeof assessmentMissedEventSchema>;

export const fileAssessmentResponseSchema = z.object({
  ok: z.literal(true),
  record: assessmentRecordSchema,
});
export type FileAssessmentResponse = z.infer<typeof fileAssessmentResponseSchema>;

/** Head records only (the row nothing supersedes), plus the miss events. */
export const episodeAssessmentsResponseSchema = z.object({
  ok: z.literal(true),
  episodeId: z.uuid(),
  protocolVersion: z.string(),
  records: z.array(assessmentRecordSchema),
  missedEvents: z.array(assessmentMissedEventSchema),
});
export type EpisodeAssessmentsResponse = z.infer<typeof episodeAssessmentsResponseSchema>;

export const sessionObservationSchema = z.object({
  id: z.uuid(),
  sessionId: z.uuid(),
  painNrs: z.number().nullable(),
  painNrsSkipped: z.boolean(),
  swellingGirthCm: z.number().nullable(),
  swellingGirthSkipped: z.boolean(),
  flexionDeg: z.number().nullable(),
  flexionSkipped: z.boolean(),
  extensionDeficitDeg: z.number().nullable(),
  extensionDeficitSkipped: z.boolean(),
  slrHeightDeg: z.number().nullable(),
  slrHeightSkipped: z.boolean(),
  slrKneeLagDeg: z.number().nullable(),
  slrKneeLagSkipped: z.boolean(),
  note: z.string().nullable(),
  provenance: z.literal('typed-entry'),
  occurredAt: z.iso.datetime(),
  recordedAt: z.iso.datetime(),
  actor: z.string(),
  supersedes: z.uuid().nullable(),
});
export type SessionObservation = z.infer<typeof sessionObservationSchema>;

export const recordSessionObservationResponseSchema = z.object({
  ok: z.literal(true),
  observation: sessionObservationSchema,
});
export type RecordSessionObservationResponse = z.infer<
  typeof recordSessionObservationResponseSchema
>;

export const sessionObservationResponseSchema = z.object({
  ok: z.literal(true),
  /** The head row, or null when no panel has been recorded yet. */
  observation: sessionObservationSchema.nullable(),
});
export type SessionObservationResponse = z.infer<typeof sessionObservationResponseSchema>;
